import { Box, Card, Dialog, Stack } from '@mui/material';
import { ReactElement, useState, useEffect, useCallback } from 'react';
import {
  CreateButton,
  Datagrid,
  EditButton,
  TextField,
  useTranslate,
  DeleteButton,
  FunctionField,
  BooleanField,
  useNotify,
  NumberField,
} from 'react-admin';
import { CustomBreadcrumbs } from '../../../../../components/custom-breadcrumbs';
import { DashboardContent } from '../../../../../layouts/dashboard';
import { Resources } from '../../resource';
import { PermissionGuard } from '../../../../../routes/components/permission-guard';
import ErrorPage403 from '../../../../error/403';
import { PermissionButton } from '../../../../../routes/components/permission-button';
import {
  getCriticalHitTiers,
  createCriticalHitTier,
  updateCriticalHitTier,
  deleteCriticalHitTier,
  CriticalHitTier,
} from '../../../services/redpacketService';
import CreateCriticalHitTier from './Create';
import EditCriticalHitTier from './Edit';

const CriticalHitTierList = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [popup, setPopup] = useState<ReactElement>();
  const [data, setData] = useState<CriticalHitTier[]>([]);
  const [loading, setLoading] = useState(false);
// 严格遵循 useCallback<T extends Function>(callback: T, deps: DependencyList): T 的类型定义
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getCriticalHitTiers();
      setData(Array.isArray(result) ? result : []);
    } catch (error: any) {
      notify(error.message || translate('module.redpacket.failedToGetData'), { type: 'error' });
      setData([]);
    } finally {
      setLoading(false);
    }
    // 核心：补充第二个参数——依赖数组（useCallback 必传）
  }, [notify,translate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (id: string) => {
    if (window.confirm(translate('module.redpacket.confirmDeletion'))) {
      try {
        await deleteCriticalHitTier(id);
        notify(translate('module.redpacket.deleteSuccess'), { type: 'success' });
        fetchData();
      } catch (error: any) {
        notify(error.message || translate('module.redpacket.deleteFailed'), { type: 'error' });
      }
    }
  };

  return (
    <PermissionGuard resource="redpacket" action="read" fallback={<ErrorPage403 />}>
      <DashboardContent>
        <CustomBreadcrumbs
          heading={translate('module.redpacket.criticalHitTierConfig')}
          links={[
            { name: translate('module.redpacket.redPacketManagement'), href: `/${Resources.redpacket}` },
            { name: translate('module.redpacket.criticalHitTierConfig') },
          ]}
          action={
            <PermissionGuard resource="redpacket" action="create" fallback={<></>}>
              <CreateButton
                variant="contained"
                size="medium"
                onClick={(e) => {
                  e.preventDefault();
                  setPopup(
                    <CreateCriticalHitTier
                      onClose={() => {
                        setPopup(undefined);
                        fetchData();
                      }}
                    />
                  );
                }}
              />
            </PermissionGuard>
          }
        />

        <Card sx={{ mt: 3, pb: 2 }}>
          <Box sx={{ overflow: 'auto' }}>
            <Datagrid rowClick={false} data={data} isLoading={loading} bulkActionButtons={false}>
              <NumberField source="amount" label={translate('module.redpacket.amount')} />
              <FunctionField
                label={translate('module.redpacket.probabilityPercent')}
                render={(record: CriticalHitTier) => `${(record.probability * 100).toFixed(2)}%`}
              />
              {/*<NumberField source="sortOrder" label="SortOrder" />*/}
              <BooleanField source="isActive" label={translate('module.redpacket.isActiveCol')} />
              <TextField source="description" label={translate('module.redpacket.descriptionCol')} />
              <FunctionField
                label={translate('module.redpacket.operation')}
                render={(record: CriticalHitTier) => (
                  <Stack direction="row" spacing={1}>
                    <PermissionButton resource="redpacket" action="update">
                      <EditButton
                        onClick={(e) => {
                          e.stopPropagation();
                          setPopup(
                            <EditCriticalHitTier
                              id={record.id}
                              record={record}
                              onClose={() => {
                                setPopup(undefined);
                                fetchData();
                              }}
                            />
                          );
                        }}
                      />
                    </PermissionButton>
                    <PermissionButton resource="redpacket" action="delete">
                      <DeleteButton
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(record.id);
                        }}
                      />
                    </PermissionButton>
                  </Stack>
                )}
              />
            </Datagrid>
          </Box>
        </Card>

        {popup && (
          <Dialog open maxWidth="md" fullWidth>
            {popup}
          </Dialog>
        )}
      </DashboardContent>
    </PermissionGuard>
  );
};

export default CriticalHitTierList;
